// src/services/CompanyGroupService.js
//
// Company groups: which company a person reports under (EPOKA, HuneX,
// MCGlobe). Internal grouping for Statistics only; the dashboard and the
// display never look at it. A person never placed in a group is in the
// default group, so nobody has to be assigned by hand to get started.
//
// Everything here is admin only except getPersonCompanyGroup, which the
// person log uses for its "Company:" line.

import apiClient from './apiClient'

// 4xx bodies carry the reason ("a group with that name already exists");
// axios throws with just the status code, so the body's message is used.
async function post(path, body, fallback) {
  let response
  try {
    response = await apiClient.post(path, body)
  } catch (err) {
    throw new Error(err.response?.data?.message ?? err.message ?? fallback)
  }
  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? fallback)
  }
  return response.data
}

/**
 * All groups, default first, plus every explicit placement.
 * { groups: [{ id, name, is_default, member_count }],
 *   members: [{ entra_object_id, group_id, group_name, email, name }] }
 */
export async function getCompanyGroups() {
  const data = await post('/GetCompanyGroups', {}, 'Could not read company groups')
  return { groups: data.groups ?? [], members: data.members ?? [] }
}

/** Just the groups, for a dropdown. */
export async function getCompanyGroupList() {
  const { groups } = await getCompanyGroups()
  return groups
}

export async function createCompanyGroup(name) {
  const data = await post('/CreateCompanyGroup', { name }, 'Could not create the group')
  return data.id
}

export async function renameCompanyGroup(id, name) {
  await post('/RenameCompanyGroup', { id, name }, 'Could not rename the group')
}

export async function setDefaultCompanyGroup(id) {
  await post('/SetDefaultCompanyGroup', { id }, 'Could not change the default group')
}

export async function deleteCompanyGroup(id) {
  await post('/DeleteCompanyGroup', { id }, 'Could not delete the group')
}

/**
 * Put a person in a group. groupId 0 (or the default's id) means "the
 * default", which removes any explicit placement.
 */
export async function setPersonCompanyGroup({ entraObjectId, groupId, email, name }) {
  await post('/SetPersonCompanyGroup', {
    entra_object_id: entraObjectId,
    group_id: groupId ?? 0,
    email: email ?? '',
    name: name ?? ''
  }, 'Could not update the person\'s group')
}

/**
 * One person's group, by object id and/or email. Returns
 * { id, name, is_default } or null.
 */
export async function getPersonCompanyGroup({ userId, email }) {
  const data = await post('/GetPersonCompanyGroup', {
    user_id: userId ?? '',
    email: email ?? ''
  }, 'Could not read the person\'s company')
  return data.company ?? null
}
